import type { Component } from 'vue';
import { AlertTriangle, BarChart3, ChartColumn, Coins, Gift, Layers, Clock, Sparkles, Calculator, TrendingDown, ArrowLeftRight, BadgeIndianRupee, HandCoins, RadioTower, Send, TowerControl, Bell, Boxes, Building2, ClipboardCheck, Gauge, HardHat, NotebookPen, CalendarCheck, CheckSquare, ClipboardList, Cog, Database, FileSignature, FileText, FileUp, Handshake, PackageCheck, LayoutDashboard, LayoutTemplate, ListChecks, MapPinned, Warehouse, Percent, Receipt, ScrollText, ShoppingCart, ShieldCheck, Target, Truck, UserRound, Users, Wallet, Zap } from 'lucide-vue-next';
import { PERMISSIONS as P, type PermissionKey } from '@bsl/shared';

export interface NavItem {
  label: string;
  to: string;
  icon: Component;
  /** Shown when the user holds any of these. Empty = everyone. */
  anyOf?: PermissionKey[];
  badge?: 'approvals' | 'notifications' | 'followups';
}

export interface NavSection {
  label?: string;
  items: NavItem[];
}

/** Sidebar entries. A module appears here only once its screens are built and working. */
export const NAVIGATION: NavSection[] = [
  {
    items: [
      { label: 'Dashboard', to: '/', icon: LayoutDashboard },
      { label: 'Approvals', to: '/approvals', icon: CheckSquare, anyOf: [P.APPROVALS_VIEW], badge: 'approvals' },
      { label: 'Ask the copilot', to: '/assistant', icon: Sparkles, anyOf: [P.AI_COPILOT] },
      {
        label: 'Reports',
        to: '/reports',
        icon: BarChart3,
        anyOf: [P.PNL_VIEW, P.LEDGER_VIEW, P.PROJECTS_VIEW, P.LEADS_VIEW, P.INVENTORY_VIEW, P.PROPOSALS_VIEW, P.SUBSIDY_VIEW, P.PROCUREMENT_VIEW, P.INVOICES_VIEW, P.EXPENSES_VIEW, P.EXPENSES_APPROVE, P.AUDIT_VIEW],
      },
      { label: 'Notifications', to: '/notifications', icon: Bell, badge: 'notifications' },
    ],
  },
  {
    label: 'CRM',
    items: [
      { label: 'Leads', to: '/crm/leads', icon: Target, anyOf: [P.LEADS_VIEW] },
      { label: 'Follow-ups', to: '/crm/follow-ups', icon: CalendarCheck, anyOf: [P.ACTIVITIES_LOG], badge: 'followups' },
      { label: 'Customers', to: '/crm/customers', icon: UserRound, anyOf: [P.CUSTOMERS_VIEW] },
      { label: 'Channel partners', to: '/crm/partners', icon: Handshake, anyOf: [P.PARTNERS_VIEW] },
    ],
  },
  {
    label: 'Sales',
    items: [
      { label: 'Proposals', to: '/proposals', icon: FileSignature, anyOf: [P.PROPOSALS_VIEW] },
      { label: 'Proposal templates', to: '/proposals/templates', icon: LayoutTemplate, anyOf: [P.PROPOSAL_TEMPLATES_MANAGE] },
      { label: 'Quotations', to: '/billing/quotations', icon: ScrollText, anyOf: [P.QUOTATIONS_VIEW] },
      { label: 'ROI comparison', to: '/sales/roi', icon: Calculator, anyOf: [P.PROPOSALS_VIEW] },
    ],
  },
  {
    label: 'Billing',
    items: [
      { label: 'Invoices', to: '/billing/invoices', icon: FileText, anyOf: [P.INVOICES_VIEW] },
      { label: 'Receipts', to: '/billing/receipts', icon: Receipt, anyOf: [P.RECEIPTS_VIEW] },
      { label: 'Receivables', to: '/billing/receivables', icon: Wallet, anyOf: [P.LEDGER_VIEW] },
      { label: 'Collections', to: '/telecom/collections', icon: HandCoins, anyOf: [P.LEDGER_VIEW] },
      { label: 'Tax rules', to: '/billing/tax-rules', icon: Percent, anyOf: [P.TAX_RULES_MANAGE, P.TAX_RULES_SIGN_OFF] },
    ],
  },
  {
    label: 'Delivery',
    items: [
      { label: 'Projects', to: '/projects', icon: Building2, anyOf: [P.PROJECTS_VIEW] },
      { label: 'Sites', to: '/projects/sites', icon: MapPinned, anyOf: [P.PROJECTS_VIEW] },
    ],
  },
  {
    label: 'Telecom',
    items: [
      { label: 'Work orders', to: '/telecom/work-orders', icon: RadioTower, anyOf: [P.WORK_ORDERS_MANAGE, P.TELECOM_BILLING, P.DISPATCH_MANAGE, P.SITE_COMPLETION_MANAGE] },
      { label: 'Telecom tracker', to: '/telecom/tracker', icon: TowerControl, anyOf: [P.WORK_ORDERS_MANAGE, P.SITE_COMPLETION_MANAGE, P.DISPATCH_RECEIVE] },
      { label: 'Dispatches', to: '/telecom/dispatches', icon: Send, anyOf: [P.DISPATCH_MANAGE, P.DISPATCH_RECEIVE] },
    ],
  },
  {
    label: 'Execution',
    items: [
      { label: 'Solar tracker', to: '/execution/tracker', icon: Gauge, anyOf: [P.PROJECTS_VIEW] },
      { label: 'Daily reports', to: '/execution/dprs', icon: NotebookPen, anyOf: [P.PROJECTS_VIEW] },
      { label: 'DISCOM / net metering', to: '/execution/discom', icon: Zap, anyOf: [P.PROJECTS_VIEW] },
      { label: 'PM Surya Ghar', to: '/execution/subsidy', icon: BadgeIndianRupee, anyOf: [P.SUBSIDY_VIEW] },
      { label: 'Crews', to: '/execution/crews', icon: HardHat, anyOf: [P.CREWS_MANAGE, P.EXECUTION_MANAGE] },
      { label: 'Checklist templates', to: '/execution/checklist-templates', icon: ClipboardCheck, anyOf: [P.CHECKLIST_TEMPLATES_MANAGE] },
    ],
  },
  {
    label: 'Costs & P&L',
    items: [
      { label: 'Expenses', to: '/finance/expenses', icon: Coins, anyOf: [P.EXPENSES_CREATE, P.EXPENSES_VIEW, P.EXPENSES_APPROVE] },
      { label: 'Labour cost', to: '/finance/labour', icon: Clock, anyOf: [P.LABOUR_MANAGE, P.LABOUR_APPROVE] },
      { label: 'Referral bonuses', to: '/finance/referrals', icon: Gift, anyOf: [P.REFERRALS_MANAGE, P.REFERRALS_APPROVE] },
      { label: 'Overheads', to: '/finance/overheads', icon: Layers, anyOf: [P.OVERHEADS_MANAGE] },
      { label: 'Project P&L', to: '/finance/pnl', icon: ChartColumn, anyOf: [P.PNL_VIEW] },
    ],
  },
  {
    label: 'Inventory',
    items: [
      { label: 'Stock', to: '/inventory', icon: Boxes, anyOf: [P.INVENTORY_VIEW] },
      { label: 'Movements', to: '/inventory/documents', icon: ArrowLeftRight, anyOf: [P.INVENTORY_VIEW] },
      { label: 'Locations', to: '/inventory/locations', icon: Warehouse, anyOf: [P.INVENTORY_VIEW] },
      { label: 'Stock alerts', to: '/inventory/alerts', icon: AlertTriangle, anyOf: [P.INVENTORY_VIEW] },
      { label: 'Shortage forecast', to: '/inventory/forecast', icon: TrendingDown, anyOf: [P.INVENTORY_VIEW] },
    ],
  },
  {
    label: 'Procurement',
    items: [
      { label: 'Purchase requests', to: '/procurement/requests', icon: ShoppingCart, anyOf: [P.PROCUREMENT_VIEW] },
      { label: 'Purchase orders', to: '/procurement/orders', icon: Truck, anyOf: [P.PROCUREMENT_VIEW] },
      { label: 'Goods receipts', to: '/procurement/receipts', icon: PackageCheck, anyOf: [P.PROCUREMENT_VIEW] },
    ],
  },
  {
    label: 'Data',
    items: [
      { label: 'Masters', to: '/masters', icon: Database, anyOf: [P.MASTERS_VIEW] },
      { label: 'Data import', to: '/imports', icon: FileUp, anyOf: [P.DATA_IMPORT, P.DATA_IMPORT_SIGNOFF] },
    ],
  },
  {
    label: 'Administration',
    items: [
      { label: 'Users', to: '/admin/users', icon: Users, anyOf: [P.USERS_VIEW] },
      { label: 'Roles & permissions', to: '/admin/roles', icon: ShieldCheck, anyOf: [P.ROLES_VIEW] },
      { label: 'Audit log', to: '/admin/audit', icon: ClipboardList, anyOf: [P.AUDIT_VIEW] },
      {
        label: 'Settings',
        to: '/admin/settings',
        icon: Cog,
        anyOf: [P.SETTINGS_VIEW, P.APPROVAL_RULES_MANAGE, P.NOTIFICATION_TEMPLATES_MANAGE, P.NUMBER_SERIES_MANAGE, P.AI_MANAGE],
      },
      { label: 'Background jobs', to: '/admin/jobs', icon: ListChecks, anyOf: [P.JOBS_VIEW] },
    ],
  },
];
